import { useState, useEffect, useCallback } from 'react';
import { Edit3, Trash2, Plus, X, Loader2, CheckCircle, AlertCircle, Receipt, Save } from 'lucide-react';
import { adminApi, Fee, FeeInput, ShopType } from '../services/adminApi';

const EMPTY_FORM: FeeInput = {
  label: '', unit: '%', shopTypes: ['mall', 'thuong'],
  optional: false, optionalLabel: '', capped: false, maxValue: 0,
  valueMall: 0, valueThuong: 0, sortOrder: 0, isActive: true,
};

export default function ChiPhi() {
  const [fees, setFees] = useState<Fee[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FeeInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const showMessage = (type: 'success' | 'error', text: string) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await adminApi.getChiPhi();
      if (res.success && res.data) setFees(res.data);
    } catch (err) {
      showMessage('error', (err as Error).message || 'Không thể tải dữ liệu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => { setEditingId(null); setForm(EMPTY_FORM); setModalOpen(true); };
  const openEdit = (f: Fee) => {
    setEditingId(f._id);
    setForm({
      label: f.label, unit: f.unit, shopTypes: f.shopTypes,
      optional: f.optional, optionalLabel: f.optionalLabel, capped: f.capped, maxValue: f.maxValue,
      valueMall: f.valueMall, valueThuong: f.valueThuong, sortOrder: f.sortOrder, isActive: f.isActive,
    });
    setModalOpen(true);
  };

  const toggleShop = (shop: ShopType) =>
    setForm((p) => ({
      ...p,
      shopTypes: p.shopTypes.includes(shop) ? p.shopTypes.filter((s) => s !== shop) : [...p.shopTypes, shop],
    }));

  const save = async () => {
    if (!form.label.trim()) { showMessage('error', 'Nhập tên phí'); return; }
    if (!form.shopTypes.length) { showMessage('error', 'Chọn ít nhất 1 shop áp dụng'); return; }
    try {
      setSaving(true);
      const body: FeeInput = {
        ...form,
        label: form.label.trim(),
        maxValue: Number(form.maxValue) || 0,
        valueMall: Number(form.valueMall) || 0,
        valueThuong: Number(form.valueThuong) || 0,
        capped: form.unit === '%' ? form.capped : false, // trần chỉ có nghĩa với %
      };
      const res = editingId ? await adminApi.updateFee(editingId, body) : await adminApi.createFee(body);
      if (res.success) {
        showMessage('success', editingId ? 'Đã cập nhật phí' : 'Đã thêm phí');
        setModalOpen(false);
        fetchData();
      } else showMessage('error', res.message || 'Lỗi khi lưu');
    } catch (err) {
      showMessage('error', (err as Error).message || 'Lỗi khi lưu');
    } finally {
      setSaving(false);
    }
  };

  const del = async (f: Fee) => {
    if (!window.confirm(`Xoá phí "${f.label}"? Sẽ không dùng để tính giá nữa.`)) return;
    try {
      const res = await adminApi.deleteFee(f._id);
      if (res.success) { showMessage('success', 'Đã xoá phí'); fetchData(); }
      else showMessage('error', res.message || 'Lỗi khi xoá');
    } catch (err) {
      showMessage('error', (err as Error).message || 'Lỗi khi xoá');
    }
  };

  const valDisplay = (v: number, unit: string) => (
    <span style={{ fontWeight: 600, color: 'var(--slate-800)' }}>
      {v.toLocaleString('vi-VN')}
      <span className="muted" style={{ fontSize: '0.78rem', marginLeft: 2 }}>{unit}</span>
    </span>
  );

  return (
    <div className="admin-container wide">
      <div className="page-head">
        <div>
          <h2 className="page-title">Quản Lý Chi Phí (Phí Tính Giá)</h2>
          <p className="page-subtitle">Admin thêm / sửa / xoá phí — dùng để tính giá bán</p>
        </div>
        <button className="btn btn-primary" onClick={openCreate}><Plus size={16} /> Thêm phí</button>
      </div>

      {message && (
        <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-error'}`}>
          {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
          {message.text}
        </div>
      )}

      {loading ? (
        <div className="center-screen">
          <Loader2 size={32} className="spin" style={{ color: 'var(--red-600)' }} />
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Tên phí</th>
                <th className="t-right">Shop Mall</th>
                <th className="t-right">Shop Thường</th>
                <th className="t-center" style={{ width: 96 }}>Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {fees.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <div className="empty-state">
                      <Receipt size={40} style={{ opacity: 0.4 }} />
                      <p>Chưa có phí nào. Bấm "Thêm phí" để tạo.</p>
                    </div>
                  </td>
                </tr>
              )}
              {fees.map((f) => (
                <tr key={f._id}>
                  <td>
                    <div style={{ fontWeight: 600, color: 'var(--slate-800)' }}>{f.label}</div>
                    <div className="muted" style={{ fontSize: '0.75rem' }}>
                      {f.feeKey} · {f.unit}
                      {f.optional && ' · tuỳ chọn'}
                      {f.capped && ` · trần ${f.maxValue.toLocaleString('vi-VN')}`}
                    </div>
                  </td>
                  <td className="t-right">{f.shopTypes.includes('mall') ? valDisplay(f.valueMall, f.unit) : <span className="muted">—</span>}</td>
                  <td className="t-right">{f.shopTypes.includes('thuong') ? valDisplay(f.valueThuong, f.unit) : <span className="muted">—</span>}</td>
                  <td>
                    <div className="row" style={{ justifyContent: 'center' }}>
                      <button className="btn-icon edit" onClick={() => openEdit(f)} title="Sửa"><Edit3 size={16} /></button>
                      <button className="btn-icon danger" onClick={() => del(f)} title="Xoá"><Trash2 size={16} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted" style={{ fontSize: '0.78rem', marginTop: 12 }}>
        Phí "tuỳ chọn" hiện dạng checkbox ở trang tính giá (vd Pi Ship, Voucher). Sửa xong nhớ vào <b>Cài đặt → Công bố</b> để người dùng nhận ngay.
      </p>

      {/* Modal thêm/sửa phí */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3>{editingId ? 'Sửa phí' : 'Thêm phí mới'}</h3>
              <button className="btn-icon" onClick={() => setModalOpen(false)}><X size={18} /></button>
            </div>
            <div className="modal-body stack" style={{ gap: 14 }}>
              <div className="field">
                <label className="field-label">Tên phí</label>
                <input className="input" value={form.label} placeholder="VD: Phí thanh toán"
                  onChange={(e) => setForm({ ...form, label: e.target.value })} />
              </div>

              <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
                <div className="field" style={{ flex: 1, margin: 0 }}>
                  <label className="field-label">Đơn vị</label>
                  <select className="input" value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value as '%' | 'VND' })}>
                    <option value="%">% (theo giá bán)</option>
                    <option value="VND">VND (cố định)</option>
                  </select>
                </div>
                <div className="field" style={{ flex: 1, margin: 0 }}>
                  <label className="field-label">Áp dụng shop</label>
                  <div className="row" style={{ gap: 16, paddingTop: 8 }}>
                    {(['mall', 'thuong'] as ShopType[]).map((s) => (
                      <label key={s} className="row" style={{ gap: 6, cursor: 'pointer', margin: 0 }}>
                        <input type="checkbox" checked={form.shopTypes.includes(s)} onChange={() => toggleShop(s)}
                          style={{ width: 16, height: 16, flexShrink: 0, accentColor: 'var(--red-600)' }} />
                        {s === 'mall' ? 'Mall' : 'Thường'}
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="row" style={{ gap: 12 }}>
                {form.shopTypes.includes('mall') && (
                  <div className="field" style={{ flex: 1, margin: 0 }}>
                    <label className="field-label">Giá trị (Mall)</label>
                    <input className="input" type="number" step="any" value={form.valueMall}
                      onChange={(e) => setForm({ ...form, valueMall: Number(e.target.value) })} />
                  </div>
                )}
                {form.shopTypes.includes('thuong') && (
                  <div className="field" style={{ flex: 1, margin: 0 }}>
                    <label className="field-label">Giá trị (Thường)</label>
                    <input className="input" type="number" step="any" value={form.valueThuong}
                      onChange={(e) => setForm({ ...form, valueThuong: Number(e.target.value) })} />
                  </div>
                )}
              </div>

              <div className="info-box stack" style={{ gap: 12 }}>
                <div className="row-between">
                  <span className="field-label" style={{ margin: 0 }}>Phí tuỳ chọn (user bật/tắt ở calculator)</span>
                  <button className={`switch${form.optional ? ' on' : ''}`} onClick={() => setForm({ ...form, optional: !form.optional })}>
                    <span className="knob" />
                  </button>
                </div>
                {form.optional && (
                  <div className="field" style={{ margin: 0 }}>
                    <label className="field-label">Nhãn checkbox</label>
                    <input className="input" value={form.optionalLabel} placeholder="VD: Pi Ship"
                      onChange={(e) => setForm({ ...form, optionalLabel: e.target.value })} />
                  </div>
                )}
              </div>

              {form.unit === '%' && (
                <div className="info-box stack" style={{ gap: 12 }}>
                  <div className="row-between">
                    <span className="field-label" style={{ margin: 0 }}>Có trần (tối đa VND)</span>
                    <button className={`switch${form.capped ? ' on' : ''}`} onClick={() => setForm({ ...form, capped: !form.capped })}>
                      <span className="knob" />
                    </button>
                  </div>
                  {form.capped && (
                    <div className="field row" style={{ margin: 0, alignItems: 'center' }}>
                      <label className="field-label" style={{ margin: 0 }}>Trần</label>
                      <input className="input tiny" type="number" step="any" value={form.maxValue}
                        onChange={(e) => setForm({ ...form, maxValue: Number(e.target.value) })} />
                      <span className="muted">VND</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-foot">
              <button className="btn btn-secondary" onClick={() => setModalOpen(false)}>Huỷ</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? <Loader2 size={18} className="spin" /> : <Save size={16} />} Lưu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
